import React, { useState, useEffect, useRef } from 'react';
import { FriendUser, FriendRequest, FollowUser } from '../../types';
import {
  fetchSocialGraph,
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  cancelFriendRequest,
  removeFriend
} from '../../services/friendService';
import {
  fetchFollowing,
  fetchFollowers,
  unfollowUser,
  followUser
} from '../../services/followService';
import { CharacterAvatar } from '../common/CharacterAvatar';
import {
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Trash2,
  Check,
  X,
  RefreshCw,
  Sparkles,
  Film,
  Mic,
  Clock,
  Send,
  Heart,
  HelpCircle
} from 'lucide-react';

interface FriendsPanelProps {
  currentUserName?: string;
  activeFriendFilter: string | null;
  onSelectFriend: (friendUsername: string) => void;
  onClearFriendFilter: () => void;
}

export const FriendsPanel: React.FC<FriendsPanelProps> = ({
  currentUserName = '',
  activeFriendFilter,
  onSelectFriend,
  onClearFriendFilter
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'friends' | 'requests' | 'following'>('friends');
  const [followSubView, setFollowSubView] = useState<'following' | 'followers'>('following');

  const [friends, setFriends] = useState<FriendUser[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<FriendRequest[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<FriendRequest[]>([]);
  const [following, setFollowing] = useState<FollowUser[]>([]);
  const [followers, setFollowers] = useState<FollowUser[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [newFriendInput, setNewFriendInput] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [confirmUnfriend, setConfirmUnfriend] = useState<string | null>(null);

  const isCheckingRef = useRef<boolean>(false);

  const loadSocialData = async () => {
    if (!currentUserName?.trim()) {
      setFriends([]);
      setIncomingRequests([]);
      setOutgoingRequests([]);
      setFollowing([]);
      setFollowers([]);
      setIsLoading(false);
      return;
    }

    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    setIsLoading(true);

    try {
      const [graphRes, followingRes, followersRes] = await Promise.all([
        fetchSocialGraph(currentUserName),
        fetchFollowing(currentUserName),
        fetchFollowers(currentUserName)
      ]);

      setFriends(graphRes.friends);
      setIncomingRequests(graphRes.incomingRequests);
      setOutgoingRequests(graphRes.outgoingRequests);
      setFollowing(followingRes);
      setFollowers(followersRes);
    } catch (err) {
      console.warn('[FriendsPanel] loadSocialData notice:', err);
    } finally {
      setIsLoading(false);
      isCheckingRef.current = false;
    }
  };

  useEffect(() => {
    loadSocialData();
  }, [currentUserName]);

  // Send friend request
  const handleSendFriendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = newFriendInput.trim();
    if (!clean) return;

    if (!currentUserName?.trim()) {
      setErrorMsg('Please sign in at the top right first to send friend requests');
      return;
    }

    if (clean.toLowerCase() === currentUserName.toLowerCase()) {
      setErrorMsg('You cannot send a friend request to yourself');
      return;
    }

    if (friends.some((f) => f.username.toLowerCase() === clean.toLowerCase())) {
      setErrorMsg(`@${clean} is already in your mutual friends list`);
      return;
    }

    if (outgoingRequests.some((r) => r.username.toLowerCase() === clean.toLowerCase())) {
      setErrorMsg(`You already sent a friend request to @${clean}`);
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const res = await sendFriendRequest(currentUserName, clean);
      if (res.success) {
        setSuccessMsg(res.message || `Friend request sent to @${clean}!`);
        setNewFriendInput('');
        await loadSocialData();
        setTimeout(() => setSuccessMsg(null), 4000);
      } else {
        setErrorMsg(res.error || `Could not find performer @${clean}`);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error connecting to server');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Accept incoming request
  const handleAcceptRequest = async (e: React.MouseEvent, requesterName: string) => {
    e.stopPropagation();
    setIsSubmitting(true);
    try {
      const res = await acceptFriendRequest(currentUserName, requesterName);
      if (res.success) {
        setSuccessMsg(`Accepted @${requesterName}'s request! You are now mutual friends.`);
        await loadSocialData();
        setTimeout(() => setSuccessMsg(null), 3000);
      } else {
        setErrorMsg(res.error || 'Failed to accept request');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error accepting request');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Decline incoming request
  const handleDeclineRequest = async (e: React.MouseEvent, requesterName: string) => {
    e.stopPropagation();
    try {
      await declineFriendRequest(currentUserName, requesterName);
      setIncomingRequests((prev) => prev.filter((r) => r.username.toLowerCase() !== requesterName.toLowerCase()));
    } catch (err) {
      console.warn('[FriendsPanel] decline notice:', err);
    }
  };

  // Cancel outgoing request
  const handleCancelRequest = async (e: React.MouseEvent, targetName: string) => {
    e.stopPropagation();
    try {
      await cancelFriendRequest(currentUserName, targetName);
      setOutgoingRequests((prev) => prev.filter((r) => r.username.toLowerCase() !== targetName.toLowerCase()));
    } catch (err) {
      console.warn('[FriendsPanel] cancel request notice:', err);
    }
  };

  // Unfriend mutual friend
  const handleRemoveFriend = async (e: React.MouseEvent, friendName: string) => {
    e.stopPropagation();
    if (confirmUnfriend !== friendName) {
      setConfirmUnfriend(friendName);
      return;
    }

    setConfirmUnfriend(null);
    if (activeFriendFilter?.toLowerCase() === friendName.toLowerCase()) {
      onClearFriendFilter();
    }

    try {
      await removeFriend(currentUserName, friendName);
      setFriends((prev) => prev.filter((f) => f.username.toLowerCase() !== friendName.toLowerCase()));
    } catch (err) {
      console.warn('[FriendsPanel] removeFriend notice:', err);
    }
  };

  // Unfollow creator
  const handleUnfollow = async (e: React.MouseEvent, targetName: string) => {
    e.stopPropagation();
    try {
      await unfollowUser(currentUserName, targetName);
      setFollowing((prev) => prev.filter((u) => u.username.toLowerCase() !== targetName.toLowerCase()));
    } catch (err) {
      console.warn('[FriendsPanel] unfollow notice:', err);
    }
  };

  // Follow back from followers
  const handleFollowBack = async (e: React.MouseEvent, targetName: string) => {
    e.stopPropagation();
    try {
      const res = await followUser(currentUserName, targetName);
      if (res.success) {
        await loadSocialData();
      }
    } catch (err) {
      console.warn('[FriendsPanel] followBack notice:', err);
    }
  };

  const totalPendingRequests = incomingRequests.length;

  return (
    <aside
      style={{
        background: 'linear-gradient(135deg, rgba(26, 22, 42, 0.85) 0%, rgba(15, 12, 28, 0.95) 100%)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(168, 85, 247, 0.25)',
        borderRadius: '16px',
        padding: '1.25rem',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.9rem'
      }}
    >
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(99, 102, 241, 0.3) 100%)',
              border: '1px solid rgba(168, 85, 247, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Users size={16} color="#c084fc" />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '0.96rem', fontWeight: 700, color: '#f3e8ff' }}>
              Friends & Social Graph
            </h3>
            <p style={{ margin: 0, fontSize: '0.70rem', color: '#94a3b8' }}>
              2-Way Duets & 1-Way Follows
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={loadSocialData}
          disabled={isLoading}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#a855f7',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            padding: '4px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.15s ease'
          }}
          title="Refresh social graph"
        >
          <RefreshCw size={13} className={isLoading ? 'spin' : ''} />
        </button>
      </div>

      {/* ── Sub-Tabs Navigation (Friends | Requests | Following) ── */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          background: 'rgba(255, 255, 255, 0.04)',
          padding: '3px',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.06)'
        }}
      >
        <button
          type="button"
          onClick={() => setActiveSubTab('friends')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            padding: '6px 4px',
            borderRadius: '8px',
            fontSize: '0.74rem',
            fontWeight: activeSubTab === 'friends' ? 700 : 500,
            background: activeSubTab === 'friends' ? 'rgba(168, 85, 247, 0.3)' : 'transparent',
            border: activeSubTab === 'friends' ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid transparent',
            color: activeSubTab === 'friends' ? '#f3e8ff' : '#94a3b8',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
        >
          <UserCheck size={12} color={activeSubTab === 'friends' ? '#c084fc' : '#94a3b8'} />
          <span>Friends</span>
          <span style={{ fontSize: '0.66rem', opacity: 0.8 }}>({friends.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('requests')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            padding: '6px 4px',
            borderRadius: '8px',
            fontSize: '0.74rem',
            fontWeight: activeSubTab === 'requests' ? 700 : 500,
            background: activeSubTab === 'requests' ? 'rgba(168, 85, 247, 0.3)' : 'transparent',
            border: activeSubTab === 'requests' ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid transparent',
            color: activeSubTab === 'requests' ? '#f3e8ff' : '#94a3b8',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            position: 'relative'
          }}
        >
          <Clock size={12} color={activeSubTab === 'requests' ? '#c084fc' : '#94a3b8'} />
          <span>Requests</span>
          {totalPendingRequests > 0 && (
            <span
              style={{
                fontSize: '0.62rem',
                fontWeight: 800,
                padding: '1px 5px',
                borderRadius: '9999px',
                background: '#ef4444',
                color: '#ffffff',
                boxShadow: '0 0 8px rgba(239, 68, 68, 0.6)'
              }}
            >
              {totalPendingRequests}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('following')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            padding: '6px 4px',
            borderRadius: '8px',
            fontSize: '0.74rem',
            fontWeight: activeSubTab === 'following' ? 700 : 500,
            background: activeSubTab === 'following' ? 'rgba(168, 85, 247, 0.3)' : 'transparent',
            border: activeSubTab === 'following' ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid transparent',
            color: activeSubTab === 'following' ? '#f3e8ff' : '#94a3b8',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
        >
          <Heart size={12} color={activeSubTab === 'following' ? '#c084fc' : '#94a3b8'} />
          <span>Follows</span>
          <span style={{ fontSize: '0.66rem', opacity: 0.8 }}>({following.length})</span>
        </button>
      </div>

      {/* ── Send Friend Request Input Form (Shown in Friends & Requests tabs) ── */}
      {activeSubTab !== 'following' && (
        <form onSubmit={handleSendFriendRequest} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input
              type="text"
              value={newFriendInput}
              onChange={(e) => {
                setNewFriendInput(e.target.value);
                if (errorMsg) setErrorMsg(null);
              }}
              placeholder="Send friend request by username..."
              style={{
                flex: 1,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                padding: '6px 10px',
                color: '#ffffff',
                fontSize: '0.76rem',
                outline: 'none'
              }}
            />
            <button
              type="submit"
              disabled={isSubmitting || !newFriendInput.trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 11px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                border: 'none',
                color: '#ffffff',
                fontSize: '0.74rem',
                fontWeight: 700,
                cursor: isSubmitting || !newFriendInput.trim() ? 'not-allowed' : 'pointer',
                opacity: isSubmitting || !newFriendInput.trim() ? 0.6 : 1
              }}
            >
              <Send size={12} />
              <span>Request</span>
            </button>
          </div>
          <span style={{ fontSize: '0.65rem', color: '#94a3b8', paddingLeft: '2px' }}>
            Direct duet challenges require mutual accepted friendship.
          </span>
        </form>
      )}

      {/* ── Status Alerts ── */}
      {errorMsg && (
        <div style={{ fontSize: '0.72rem', color: '#f87171', padding: '6px 8px', borderRadius: '6px', background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div style={{ fontSize: '0.72rem', color: '#34d399', padding: '6px 8px', borderRadius: '6px', background: 'rgba(52, 211, 153, 0.12)', border: '1px solid rgba(52, 211, 153, 0.2)' }}>
          {successMsg}
        </div>
      )}

      {/* ── TAB 1: Mutual Accepted Friends ── */}
      {activeSubTab === 'friends' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '340px', overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '1.25rem', color: '#94a3b8', fontSize: '0.78rem' }}>
              <RefreshCw size={14} className="spin" style={{ marginBottom: '4px' }} />
              <div>Loading friends...</div>
            </div>
          ) : !currentUserName?.trim() ? (
            <div style={{ textAlign: 'center', padding: '1.25rem 0.75rem', borderRadius: '8px', border: '1px dashed rgba(255, 255, 255, 0.1)', color: '#94a3b8', fontSize: '0.74rem' }}>
              Sign in at the top right to build your mutual friend network.
            </div>
          ) : friends.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.25rem 0.75rem', borderRadius: '8px', border: '1px dashed rgba(255, 255, 255, 0.1)', color: '#94a3b8', fontSize: '0.74rem' }}>
              No mutual friends yet. Send a friend request above or follow creators to get started!
            </div>
          ) : (
            friends.map((friend) => {
              const isSelected = activeFriendFilter?.toLowerCase() === friend.username.toLowerCase();
              const isConfirming = confirmUnfriend?.toLowerCase() === friend.username.toLowerCase();

              return (
                <div
                  key={friend.username}
                  onClick={() => {
                    if (isSelected) {
                      onClearFriendFilter();
                    } else {
                      onSelectFriend(friend.username);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderRadius: '10px',
                    background: isSelected
                      ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(99, 102, 241, 0.22) 100%)'
                      : 'rgba(255, 255, 255, 0.03)',
                    border: isSelected
                      ? '1.5px solid #c084fc'
                      : '1px solid rgba(255, 255, 255, 0.06)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    boxShadow: isSelected ? '0 0 12px rgba(168, 85, 247, 0.25)' : 'none'
                  }}
                  title={isSelected ? 'Click to clear friend filter' : `Click to filter clips by @${friend.username}`}
                >
                  {/* Avatar & Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <div style={{ position: 'relative' }}>
                      <CharacterAvatar avatarId={friend.avatarUrl || 'char-maverick'} size="sm" />
                      {friend.isOnline && (
                        <span
                          style={{
                            position: 'absolute',
                            bottom: '0',
                            right: '0',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#10b981',
                            border: '1.5px solid #1a162a'
                          }}
                          title="Active recently"
                        />
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: isSelected ? '#f3e8ff' : '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        @{friend.username}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.66rem', color: '#94a3b8', marginTop: '1px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <Film size={9} /> {friend.clipsCount || 0}
                        </span>
                        <span>•</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <Mic size={9} /> {friend.takesCount || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions: Filter Active indicator or Unfriend */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {isSelected && (
                      <span style={{ fontSize: '0.66rem', color: '#c084fc', fontWeight: 700 }}>
                        Active
                      </span>
                    )}

                    {isConfirming ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <button
                          type="button"
                          onClick={(e) => handleRemoveFriend(e, friend.username)}
                          style={{
                            background: '#ef4444',
                            border: 'none',
                            color: '#ffffff',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '0.64rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                          title="Confirm remove friend"
                        >
                          Unfriend?
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmUnfriend(null);
                          }}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            color: '#e2e8f0',
                            borderRadius: '4px',
                            padding: '2px 5px',
                            fontSize: '0.64rem',
                            cursor: 'pointer'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleRemoveFriend(e, friend.username)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '4px',
                          display: 'flex'
                        }}
                        title={`Remove @${friend.username} from friends`}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── TAB 2: Friend Requests (Incoming & Sent) ── */}
      {activeSubTab === 'requests' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '340px', overflowY: 'auto' }}>
          {/* Incoming Requests Section */}
          <div>
            <div style={{ fontSize: '0.70rem', fontWeight: 700, color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Incoming Requests</span>
              <span style={{ background: 'rgba(168, 85, 247, 0.2)', padding: '1px 6px', borderRadius: '9999px', fontSize: '0.64rem', color: '#e9d5ff' }}>
                {incomingRequests.length}
              </span>
            </div>

            {incomingRequests.length === 0 ? (
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', padding: '8px 10px', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.02)', textAlign: 'center' }}>
                No incoming friend requests.
              </div>
            ) : (
              incomingRequests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderRadius: '10px',
                    background: 'rgba(168, 85, 247, 0.08)',
                    border: '1px solid rgba(168, 85, 247, 0.25)',
                    marginBottom: '5px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <CharacterAvatar avatarId={req.avatarUrl || 'char-maverick'} size="sm" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.80rem', fontWeight: 700, color: '#f3e8ff' }}>
                        @{req.username}
                      </div>
                      <div style={{ fontSize: '0.64rem', color: '#94a3b8' }}>
                        Wants to collaborate
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                      type="button"
                      onClick={(e) => handleAcceptRequest(e, req.username)}
                      disabled={isSubmitting}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        color: '#ffffff',
                        fontSize: '0.70rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                      title="Accept friend request"
                    >
                      <Check size={11} />
                      <span>Accept</span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeclineRequest(e, req.username)}
                      style={{
                        padding: '4px 6px',
                        borderRadius: '6px',
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#94a3b8',
                        fontSize: '0.70rem',
                        cursor: 'pointer'
                      }}
                      title="Decline friend request"
                    >
                      <X size={11} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Outgoing Requests Section */}
          <div>
            <div style={{ fontSize: '0.70rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Sent Requests</span>
              <span style={{ background: 'rgba(255, 255, 255, 0.08)', padding: '1px 6px', borderRadius: '9999px', fontSize: '0.64rem', color: '#cbd5e1' }}>
                {outgoingRequests.length}
              </span>
            </div>

            {outgoingRequests.length === 0 ? (
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', padding: '8px 10px', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.02)', textAlign: 'center' }}>
                No pending sent requests.
              </div>
            ) : (
              outgoingRequests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    marginBottom: '5px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <CharacterAvatar avatarId={req.avatarUrl || 'char-maverick'} size="sm" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.80rem', fontWeight: 700, color: '#e2e8f0' }}>
                        @{req.username}
                      </div>
                      <div style={{ fontSize: '0.64rem', color: '#a855f7' }}>
                        Pending approval...
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => handleCancelRequest(e, req.username)}
                    style={{
                      padding: '3px 7px',
                      borderRadius: '6px',
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#94a3b8',
                      fontSize: '0.66rem',
                      cursor: 'pointer'
                    }}
                    title="Cancel friend request"
                  >
                    Cancel
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── TAB 3: 1-Way Follows (Following & Followers) ── */}
      {activeSubTab === 'following' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '340px', overflowY: 'auto' }}>
          {/* Sub-view switcher */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              onClick={() => setFollowSubView('following')}
              style={{
                flex: 1,
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '0.70rem',
                fontWeight: followSubView === 'following' ? 700 : 500,
                background: followSubView === 'following' ? 'rgba(168, 85, 247, 0.25)' : 'rgba(255, 255, 255, 0.04)',
                border: followSubView === 'following' ? '1px solid rgba(168, 85, 247, 0.4)' : '1px solid transparent',
                color: followSubView === 'following' ? '#f3e8ff' : '#94a3b8',
                cursor: 'pointer'
              }}
            >
              Following ({following.length})
            </button>
            <button
              type="button"
              onClick={() => setFollowSubView('followers')}
              style={{
                flex: 1,
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '0.70rem',
                fontWeight: followSubView === 'followers' ? 700 : 500,
                background: followSubView === 'followers' ? 'rgba(168, 85, 247, 0.25)' : 'rgba(255, 255, 255, 0.04)',
                border: followSubView === 'followers' ? '1px solid rgba(168, 85, 247, 0.4)' : '1px solid transparent',
                color: followSubView === 'followers' ? '#f3e8ff' : '#94a3b8',
                cursor: 'pointer'
              }}
            >
              Followers ({followers.length})
            </button>
          </div>

          {/* List Content */}
          {followSubView === 'following' ? (
            following.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.25rem 0.75rem', borderRadius: '8px', border: '1px dashed rgba(255, 255, 255, 0.1)', color: '#94a3b8', fontSize: '0.74rem' }}>
                You are not following any creators yet. Explore the Community Showcase and click <b>+ Follow</b> to populate this feed!
              </div>
            ) : (
              following.map((creator) => (
                <div
                  key={creator.username}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.06)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <CharacterAvatar avatarId={creator.avatarUrl || 'char-maverick'} size="sm" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.80rem', fontWeight: 700, color: '#f3e8ff' }}>
                        @{creator.username}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.64rem', color: '#94a3b8' }}>
                        <span>{creator.clipsCount || 0} clips</span>
                        <span>•</span>
                        <span>{creator.takesCount || 0} takes</span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => handleUnfollow(e, creator.username)}
                    style={{
                      padding: '3px 7px',
                      borderRadius: '6px',
                      background: 'rgba(239, 68, 68, 0.12)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#fca5a5',
                      fontSize: '0.66rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                    title={`Unfollow @${creator.username}`}
                  >
                    Unfollow
                  </button>
                </div>
              ))
            )
          ) : (
            followers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.25rem 0.75rem', borderRadius: '8px', border: '1px dashed rgba(255, 255, 255, 0.1)', color: '#94a3b8', fontSize: '0.74rem' }}>
                No followers yet. Publish creative dubs to the showcase to gain followers!
              </div>
            ) : (
              followers.map((follower) => {
                const isAlreadyFollowing = following.some((f) => f.username.toLowerCase() === follower.username.toLowerCase());
                return (
                  <div
                    key={follower.username}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 10px',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.06)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <CharacterAvatar avatarId={follower.avatarUrl || 'char-maverick'} size="sm" />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.80rem', fontWeight: 700, color: '#f3e8ff' }}>
                          @{follower.username}
                        </div>
                        <div style={{ fontSize: '0.64rem', color: '#94a3b8' }}>
                          Follows your work
                        </div>
                      </div>
                    </div>

                    {!isAlreadyFollowing && (
                      <button
                        type="button"
                        onClick={(e) => handleFollowBack(e, follower.username)}
                        style={{
                          padding: '3px 8px',
                          borderRadius: '6px',
                          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(99, 102, 241, 0.3) 100%)',
                          border: '1px solid rgba(168, 85, 247, 0.5)',
                          color: '#f3e8ff',
                          fontSize: '0.66rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        + Follow Back
                      </button>
                    )}
                  </div>
                );
              })
            )
          )}
        </div>
      )}
    </aside>
  );
};
